import React, { useState, useEffect } from 'react';
import '../css/App.css';
import axios from 'axios';
import API_BASE from './apiBase';
import { styles } from './styles';
import { Eye, Edit, Activity, FileText, Shield, Languages, CheckCircle, Lock, Trash2 } from 'lucide-react';
import MultilingualConsentPage from './MultilingualConsentPage';
import VersionManagementModal from './VersionManagementModal';
import { getVersion } from './versionService';
import { compareConsentVersions, getConsentVersionUpdatePlan } from './consentVersionUtils';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Swal from 'sweetalert2';

function NotificationPage({ onBack, tenantId }) {
	const effectiveTenantId = tenantId || Number(localStorage.getItem('tenant_id')) || null;
	const [rows, setRows] = useState([]);
	const [contentMap, setContentMap] = useState({});
	const [metaMap, setMetaMap] = useState({});
	const [noticeIdMap, setNoticeIdMap] = useState({});
	const [consentParentIdMap, setConsentParentIdMap] = useState({});
	const [consentStateMap, setConsentStateMap] = useState({});
	const [selectedKey, setSelectedKey] = useState(null);
	const [editorValue, setEditorValue] = useState('');
	const [loadingSend, setLoadingSend] = useState(false);
	const [generatingPdfs, setGeneratingPdfs] = useState(false);
	const [cachedPdfs, setCachedPdfs] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState('view');
	const [modalKey, setModalKey] = useState(null);
	const [hoveredCard, setHoveredCard] = useState(null);
	const [version, setVersion] = useState('...');
	const [editVersionValue, setEditVersionValue] = useState('');
	const [versionModalOpen, setVersionModalOpen] = useState(false);
	const [versionModalTarget, setVersionModalTarget] = useState(null);
	const [versionManagementTarget, setVersionManagementTarget] = useState(null);

	// New-privacy modal state
	const [newModalOpen, setNewModalOpen] = useState(false);
	const [newTitle, setNewTitle] = useState('');
	const [newContentVal, setNewContentVal] = useState('');
	const [newConsentType, setNewConsentType] = useState('');
	const [newIsCustom, setNewIsCustom] = useState(false);
	const [consentTypesList, setConsentTypesList] = useState([]);

	// Multilingual consent modal state
	const [multilingualOpen, setMultilingualOpen] = useState(false);
	const [multilingualTarget, setMultilingualTarget] = useState(null);

	const quillModules = {
		toolbar: [
			['bold', 'italic', 'underline'],
			[{ color: [] }]
		]
	};
	const quillFormats = ['bold', 'italic', 'underline', 'color'];

	const sanitize = (html) => {
		try {
			const parser = new DOMParser();
			const doc = parser.parseFromString(html || '', 'text/html');
			const allowed = new Set(['B', 'I', 'U', 'STRONG', 'EM', 'SPAN', 'P', 'BR', 'H1', 'H2', 'H3']);
			const walk = (node) => {
				const children = Array.from(node.childNodes);
				for (const child of children) {
					if (child.nodeType === Node.ELEMENT_NODE) {
						if (!allowed.has(child.tagName)) {
							const frag = document.createDocumentFragment();
							while (child.firstChild) frag.appendChild(child.firstChild);
							child.replaceWith(frag);
						} else {
							const style = child.getAttribute('style');
							if (style) {
								const safe = style.split(';').map(s => s.trim()).filter(s => {
									if (!s) return false;
									const [k, v] = s.split(':').map(t => t.trim());
									if (!k || !v) return false;
									if (k.toLowerCase() !== 'color') return false;
									return true;
								}).join(';');
								if (safe) child.setAttribute('style', safe);
								else child.removeAttribute('style');
							}
							walk(child);
						}
					} else if (child.nodeType === Node.TEXT_NODE) {
						// ok
					} else {
						child.remove();
					}
				}
			};
			walk(doc.body);
			return doc.body.innerHTML;
		} catch (e) {
			return '';
		}
	};

	const getNoticeKey = (r) => {
		return (r && (r.consent_type || r.title || r.type || r.consent_type_name) || '').toString().trim();
	};

	// Data breach notice template (HTML)
	const dataBreachTemplate = `
	<div>
		<div style="font-weight:700; font-size:1.05rem; margin-bottom:8px">[Company Letterhead/Logo]</div>
		<h2>NOTICE OF DATA BREACH</h2>
		<p><strong>Date:</strong> [Date]</p>
		<p>Subject: Notification of Data Security Breach</p>
		<p>Dear [Recipient Name],</p>
		<p>We are writing to inform you about a data security breach that occurred on <strong>[date of breach]</strong>, which may have impacted your personal data. Safeguarding your information is of utmost importance to us, and we deeply regret any inconvenience caused by this incident.</p>
		<h3>Circumstances of the Data Breach</h3>
		<p>On <strong>[specific date]</strong>, a security incident was identified that resulted in unauthorized access to specific systems. Immediate remedial actions were undertaken to contain the breach, and an exhaustive investigation was initiated to ascertain its scope and impact.</p>
		<h3>Nature of Compromised Information</h3>
		<ul>
			<li>[Type of information, e.g., name, email address, phone number, etc.]</li>
			<li>[Type of sensitive information, if applicable, e.g., financial details, identification numbers, etc.]</li>
		</ul>
		<p>We confirm that [specific sensitive data, e.g., credit card information] was not compromised [if applicable].</p>
		<h3>Remedial Measures Undertaken</h3>
		<ol>
			<li>Containment and Remediation: Immediate steps were taken to secure affected systems and prevent further unauthorized access.</li>
			<li>Notification to Authorities: The breach has been reported to the Data Protection Board of India, as mandated by the Digital Personal Data Protection Act, 2023 ("DPDPA").</li>
			<li>Enhanced Security Measures: Additional protocols have been implemented, including system updates, increased monitoring, and enhanced employee training on data security.</li>
		</ol>
		<h3>Recommended Actions for Affected Individuals</h3>
		<ol>
			<li>Change Your Passwords: Update passwords for any accounts associated with this breach.</li>
			<li>Monitor Financial Accounts: Regularly check your financial accounts for any unusual activity.</li>
			<li>Beware of Phishing Attempts: Be cautious of unsolicited communications requesting personal or financial information.</li>
		</ol>
		<h3>Contact Details for Assistance</h3>
		<p>If you have any questions or require support, please contact us using the details below:</p>
		<ul>
			<li>Email: [Insert contact email]</li>
			<li>Phone: [Insert contact number]</li>
			<li>Address: [Insert company address]</li>
		</ul>
		<p>We sincerely apologize for any inconvenience this may have caused and appreciate your understanding.</p>
		<p>Sincerely,<br>[Your Name]<br>[Your Title]<br>[Company Name]</p>
	</div>
	`;

	// Fetch privacy notices and populate maps
	const fetchNotices = async () => {
		try {
			const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
			let url = `${API_BASE}/user/privacy-notices`;
			if (tenant_id) url += `?tenant_id=${tenant_id}`;
			const res = await axios.get(url);
			let data = [];
			if (Array.isArray(res.data)) data = res.data;
			else if (Array.isArray(res.data?.privacy_notices)) data = res.data.privacy_notices;
			else if (Array.isArray(res.data?.notices)) data = res.data.notices;
			else if (Array.isArray(res.data?.data)) data = res.data.data;
			else if (Array.isArray(res.data?.rows)) data = res.data.rows;
			// Show all notices returned by backend for this tenant (do not hide inactive)
			const activeData = (data || []);
			setRows(activeData || []);
			const cm = {};
			const mm = {};
			const idm = {};
			for (const r of (activeData || [])) {
				const ct = r.consent_type || r.title || r.type || r.consent_type_name;
				if (!ct) continue;
				const key = r.id ? String(r.id) : ct;
				const msg = r.consent_message || r.content || r.message || r.consent || '';
				if (msg) cm[key] = msg;
				if (ct && !cm[ct]) cm[ct] = msg;
				let color = '#2563eb';
				let usage = 0;
				let noticeStatus = 'under_process';
				let noticeLocked = false;
				try {
					let cdObj = null;
					if (r.card_data) {
						if (typeof r.card_data === 'string') {
							cdObj = JSON.parse(r.card_data || '{}');
						} else {
							cdObj = r.card_data;
						}
					}
					if (cdObj) {
						color = cdObj.color || color;
						usage = Number(cdObj.usage || usage);
						if (cdObj.status) noticeStatus = String(cdObj.status).toLowerCase();
						if (cdObj.locked !== undefined) noticeLocked = Boolean(cdObj.locked);
					}
					color = color || '#2563eb';
				} catch (e) { color = color; }

				if (r.status) noticeStatus = String(r.status).toLowerCase();
				if (r.locked !== undefined) noticeLocked = Boolean(r.locked);
				if (noticeStatus === 'accepted') noticeLocked = true;

				mm[key] = {
					id: r.id,
					label: r.title || ct || key,
					consent_type: ct,
					color,
					usage: Number(usage || 0),
					description: r.description || msg || '',
					version: r.version || r.version_tag || r.version_number || '1.0',
					status: noticeStatus,
					locked: noticeLocked
				};
				if (ct && !mm[ct]) mm[ct] = mm[key];
				if (r.id) idm[key] = r.id;
				if (ct && r.id && !idm[ct]) idm[ct] = r.id;
				if (!cm[key] && r.description) cm[key] = r.description;
			}
			setContentMap(cm);
			setMetaMap(mm);
			setNoticeIdMap(idm);
			// Multilingual variants use the main new_consent row as their parent.
			// privacy_notices ids are a different table and cannot be used here.
			try {
				if (tenant_id) {
					const consentRes = await axios.get(`${API_BASE}/manager/new-consents`, { params: { tenant_id } });
					const consentRows = Array.isArray(consentRes?.data?.data) ? consentRes.data.data : [];
					const parentMap = {};
					const stateMap = {};
					for (const row of consentRows) {
						if (!row?.id || row.parent_id != null) continue;
						const rowKey = normalizeKey(row.consent_type || row.name || '');
						const rowVer = row.consent_version || row.version || '1.0';
						const rowStatus = String(row.status || 'under_process').toLowerCase();

						const keysToRegister = new Set();
						if (rowKey) keysToRegister.add(rowKey);
						if (row.consent_type) {
							keysToRegister.add(normalizeKey(row.consent_type));
							keysToRegister.add(String(row.consent_type).trim().toLowerCase());
						}
						if (row.name) keysToRegister.add(normalizeKey(row.name));
						if (row.id) keysToRegister.add(String(row.id));

						for (const k of keysToRegister) {
							if (!k) continue;
							const existing = stateMap[k];
							let isHigher = false;
							if (existing && existing.version && rowVer) {
								try {
									isHigher = compareConsentVersions(rowVer, existing.version) === 1;
								} catch (e) {
									isHigher = false;
								}
							}

							if (!existing || isHigher) {
								parentMap[k] = row.id;
								stateMap[k] = {
									id: row.id,
									status: rowStatus,
									version: rowVer,
									locked: Boolean(row.locked || rowStatus === 'accepted'),
								};
							}
						}
					}
					setConsentParentIdMap(parentMap);
					setConsentStateMap(stateMap);
					setMetaMap(prev => {
						const updated = { ...prev };
						for (const key of Object.keys(updated)) {
							const rawCt = updated[key]?.consent_type || key;
							const noticeIdStr = updated[key]?.id ? String(updated[key].id) : null;
							const labelStr = updated[key]?.label ? normalizeKey(updated[key].label) : null;

							const nk1 = normalizeKey(rawCt);
							const nk2 = normalizeKey(key);
							const nk3 = String(key).trim().toLowerCase();

							const stateInfo = stateMap[nk1] || stateMap[nk2] || stateMap[nk3] || (noticeIdStr ? stateMap[noticeIdStr] : null) || (labelStr ? stateMap[labelStr] : null);

							if (stateInfo) {
								updated[key] = {
									...updated[key],
									version: stateInfo.version || updated[key].version,
									status: stateInfo.status || 'under_process',
									locked: Boolean(stateInfo.locked)
								};
							}
						}
						return updated;
					});
				}
			} catch (e) {
				console.warn('Failed to resolve new_consent parent ids', e);
			}
			setNoticeIdMap(idm);
			// fetch lightweight new_consent types for dropdown
			try {
				const tres = await axios.get(`${API_BASE}/user/consent/new_types`, { params: { tenant_id } });
				if (tres && tres.data && Array.isArray(tres.data.types)) setConsentTypesList(tres.data.types);
			} catch (e) {
				// ignore
			}
			const saved = localStorage.getItem('selected_consent_for_notification');
			if (saved) {
				try {
					const obj = JSON.parse(saved);
					if (obj && obj.consent_type) {
						setSelectedKey(obj.consent_type);
						setEditorValue(cm[obj.consent_type] || mm[obj.consent_type]?.description || '');
					}
				} catch (e) { }
			}
		} catch (e) {
			console.warn('Failed to fetch consents', e);
		}
	};

	const handleAcceptNotice = async (key) => {
		const rawCt = metaMap[key]?.consent_type || key;
		let state = consentStateMap[normalizeKey(rawCt)] || consentStateMap[normalizeKey(key)];
		if (!state?.id) {
			try {
				const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
				if (tenant_id) {
					const syncRes = await axios.post(`${API_BASE}/manager/new-consent`, {
						tenant_id,
						consent_type: rawCt,
						consent_message: contentMap[key] || metaMap[key]?.description || '',
						consent_metadata: JSON.stringify({ label: metaMap[key]?.label || rawCt }),
						status: 'under_process',
					}, { headers: { 'X-Tenant-ID': tenant_id } });
					if (syncRes?.data?.id) {
						state = { id: syncRes.data.id, version: '1.0' };
					}
				}
			} catch (e) {
				console.warn('Failed auto-syncing consent parent', e);
			}
		}
		if (!state?.id) return Swal.fire({ icon: 'error', title: 'Accept Failed', text: 'Consent template parent not found', customClass: { popup: 'swal-popup-custom' } });
		const result = await Swal.fire({ title: 'Accept and lock notice?', text: 'After locking, this notice cannot be edited or deleted.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Accept & Lock', confirmButtonColor: '#059669', customClass: { popup: 'swal-popup-custom' } });
		if (!result.isConfirmed) return;
		try {
			const headers = { 'Content-Type': 'application/json' };
			const token = localStorage.getItem('token');
			if (token) headers.Authorization = `Bearer ${token}`;
			const response = await fetch(`${API_BASE}/manager/accept-consent-version`, {
				method: 'POST',
				headers,
				body: JSON.stringify({ id: state.id, tenant_id: Number(localStorage.getItem('tenant_id')), consent_type: rawCt, consent_version: state.version || '1.0' }),
			});
			const body = await response.json().catch(() => ({}));
			if (!response.ok) throw new Error(body?.detail || 'Failed to accept and lock notice');
			const existingId = noticeIdMap[key] || (metaMap[key] && metaMap[key].id) || null;
			if (existingId) {
				try {
					const cardDataObj = {
						format: 'pdf_cached',
						usage: (metaMap[key] && metaMap[key].usage) || 0,
						color: (metaMap[key] && metaMap[key].color) || null,
						label: metaMap[key]?.label || key,
						status: 'accepted',
						locked: true
					};
					await axios.put(`${API_BASE}/manager/privacy-notices/${existingId}`, {
						status: 'accepted',
						card_data: cardDataObj
					});
				} catch (pe) {
					console.warn('Failed setting privacy notice row accepted state', pe);
				}
			}
			setConsentStateMap(prev => ({
				...prev,
				[normalizeKey(rawCt)]: {
					...(prev[normalizeKey(rawCt)] || {}),
					id: state.id,
					status: 'accepted',
					locked: true,
					version: state.version || '1.0'
				}
			}));
			setMetaMap(prev => ({
				...prev,
				[key]: {
					...(prev[key] || {}),
					status: 'accepted',
					locked: true
				}
			}));
			await fetchNotices();
			Swal.fire({ icon: 'success', title: 'Accepted & Locked', text: 'This notice is now locked.', timer: 1600, showConfirmButton: false, customClass: { popup: 'swal-popup-custom' } });
		} catch (error) {
			Swal.fire({ icon: 'error', title: 'Accept Failed', text: error.message, customClass: { popup: 'swal-popup-custom' } });
		}
	};

	const isNoticeLocked = (key) => {
		const rawCt = metaMap[key]?.consent_type || key;
		return Boolean(consentStateMap[normalizeKey(rawCt)]?.locked || consentStateMap[normalizeKey(key)]?.locked);
	};

	useEffect(() => { fetchNotices(); }, []);

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const v = await getVersion();
				if (mounted) setVersion(v);
			} catch (e) { }
		})();
		return () => { mounted = false; };
	}, []);

	// Initial load of cached PDF keys (auto-PDF generation loop removed for fast page loading)
	useEffect(() => {
		try {
			const keys = [];
			for (let i = 0; i < localStorage.length; i++) {
				const k = localStorage.key(i);
				if (k && k.toString().toLowerCase().includes('notification_pdf')) keys.push(k);
			}
			setCachedPdfs(keys);
		} catch (e) {
			console.warn('Failed to list cached PDFs', e);
		}
	}, []);

	const regenerateAllPdfs = async (force = false) => {
		setGeneratingPdfs(true);
		try {
			const types = Object.keys(contentMap || {});
			for (const t of types) {
				const nk = (t || '').toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/gi, '');
				if (!nk) continue;
				const key = `notification_pdf_${nk}`;
				if (!force && localStorage.getItem(key)) continue;
				const msg = contentMap[t] || '';
				try {
					let res;
					if (msg) {
						const body = new URLSearchParams();
						body.append('consent_type', t);
						body.append('message', msg);
						if (effectiveTenantId) body.append('tenant_id', String(effectiveTenantId));
						res = await axios.post(`${API_BASE}/manager/consent/template_pdf_custom`, body, { responseType: 'blob', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, timeout: 20000 });
					} else {
						const url = effectiveTenantId
							? `${API_BASE}/manager/consent/template_pdf?consent_type=${encodeURIComponent(t)}&tenant_id=${encodeURIComponent(effectiveTenantId)}`
							: `${API_BASE}/manager/consent/template_pdf?consent_type=${encodeURIComponent(t)}`;
						res = await axios.get(url, { responseType: 'blob', timeout: 20000 });
					}
					if (res && res.data) {
						const blob = res.data;
						const reader = new FileReader();
						await new Promise((resolve, reject) => {
							reader.onload = function (e) {
								try {
									const b64 = e.target.result.split(',')[1];
									const store = { filename: `${nk}.pdf`, data: b64 };
									localStorage.setItem(key, JSON.stringify(store));
									resolve();
								} catch (err) { console.warn('Failed to cache PDF', t, err); reject(err); }
							};
							reader.onerror = reject;
							reader.readAsDataURL(blob);
						});
						// update cached list
						setCachedPdfs(prev => { const p = Array.from(new Set([...(prev || []), key])); return p; });
					}
				} catch (err) {
					console.warn('Failed to generate PDF for', t, err);
				}
			}
		} catch (e) {
			console.warn('regenerateAllPdfs failed', e);
		} finally {
			setGeneratingPdfs(false);
		}
	};

	const refreshCachedPdfsList = () => {
		const keys = [];
		for (let i = 0; i < localStorage.length; i++) {
			const k = localStorage.key(i);
			if (k && k.toString().toLowerCase().includes('notification_pdf')) keys.push(k);
		}
		setCachedPdfs(keys);
	};

	const previewText = (html, max = 140) => {
		try {
			const d = document.createElement('div');
			d.innerHTML = html || '';
			const t = d.textContent || d.innerText || '';
			if (t.length <= max) return t;
			return t.slice(0, max - 1).trim() + '…';
		} catch (e) { return '' }
	};

	function normalizeKey(s) {
		return (s || '').toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/gi, '');
	}

	const getNoticeCardIcon = (key, color) => {
		const k = (key || '').toString().toLowerCase();
		if (k.includes('cookie') || k.includes('banner')) return <Shield size={22} strokeWidth={2} />;
		if (k.includes('breach') || k.includes('alert')) return <Activity size={22} strokeWidth={2} />;
		if (k.includes('user') || k.includes('account') || k.includes('employee')) return <Shield size={22} strokeWidth={2} />;
		return <FileText size={22} strokeWidth={2} />;
	};

	const handleCardClick = (key) => {
		setSelectedKey(key);
		// setEditorValue(contentMap[key] || '');
		// localStorage.setItem('selected_consent_for_notification', JSON.stringify({ consent_type: key, consent_message: contentMap[key] || '' }));
		setEditorValue(contentMap[key] || metaMap[key]?.description || '');
		localStorage.setItem('selected_consent_for_notification', JSON.stringify({ consent_type: key, consent_message: contentMap[key] || metaMap[key]?.description || '' }));
	};

	const handleSave = () => {
		if (!selectedKey) return Swal.fire({ icon: 'warning', title: 'No selection', text: 'Select a consent first', customClass: { popup: 'swal-popup-custom' } });
		const safe = sanitize(editorValue);
		setContentMap(prev => ({ ...prev, [selectedKey]: safe }));
		localStorage.setItem('last_saved_notification', JSON.stringify({ consent_type: selectedKey, consent_message: safe }));
		localStorage.setItem('selected_consent_for_notification', JSON.stringify({ consent_type: selectedKey, consent_message: safe }));
		// After saving locally, request server to render PDF and cache it in localStorage for auto-attach
		(async () => {
			try {
				const body = new URLSearchParams();
				body.append('consent_type', selectedKey);
				body.append('message', safe);
				if (effectiveTenantId) body.append('tenant_id', String(effectiveTenantId));
				const res = await axios.post(`${API_BASE}/manager/consent/template_pdf_custom`, body, { responseType: 'blob', headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
				if (res && res.data) {
					const blob = res.data;
					const reader = new FileReader();
					reader.onload = function (e) {
						try {
							const b64 = e.target.result.split(',')[1];
							const norm = (selectedKey || '').toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/gi, '');
							const store = { filename: `${norm || selectedKey}.pdf`, data: b64 };
							localStorage.setItem(`notification_pdf_${norm}`, JSON.stringify(store));
							console.debug('Cached generated PDF for', selectedKey, '->', `notification_pdf_${norm}`);
						} catch (err) { console.warn('Failed to store generated PDF', err); }
					};
					reader.readAsDataURL(blob);
				}
			} catch (e) {
				console.warn('Failed to generate PDF for saved notification', e);
			}
		})();
		// Persist notice metadata to backend so it appears in `privacy_notices`
		(async () => {
			try {
				const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
				if (!tenant_id) {
					console.warn('No tenant_id in localStorage; cannot save privacy notice to server');
					Swal.fire({ icon: 'error', title: 'Tenant not set', text: 'Sign in or set tenant before saving to database', customClass: { popup: 'swal-popup-custom' } });
					return;
				}
				const created_by = Number(localStorage.getItem('user_id')) || null;
				const payload = {
					tenant_id,
					// title: selectedKey,
					title: (metaMap[selectedKey] && metaMap[selectedKey].label) || selectedKey,
					content: safe,
					description: safe,
					consent_type: selectedKey,
					consent_message: safe,
					declaration_signature: localStorage.getItem('declaration_signature') || 'Whitehats Technologies',
					html: safe,
					card_data: {
						format: 'pdf_cached',
						usage: (metaMap[selectedKey] && metaMap[selectedKey].usage) || 0,
						color: (metaMap[selectedKey] && metaMap[selectedKey].color) || null,
						label: (metaMap[selectedKey] && metaMap[selectedKey].label) || selectedKey
					},
					created_by: created_by
				};
				// const res = await axios.post(`${API_BASE}/manager/privacy-notices`, payload);
				// If an existing notice id exists for this consent type, update via PUT
				const existingId = noticeIdMap[selectedKey];
				let res;
				if (existingId) {
					res = await axios.put(`${API_BASE}/manager/privacy-notices/${existingId}`, payload);
				} else {
					try {
						res = await axios.post(`${API_BASE}/manager/privacy-notices`, payload);
					} catch (err) {
						const resolvedId = noticeIdMap[selectedKey] || null;
						if (resolvedId) {
							res = await axios.put(`${API_BASE}/manager/privacy-notices/${resolvedId}`, payload);
						} else {
							throw err;
						}
					}
				}
				if (res && res.data && res.data.success) console.debug('Saved privacy notice to server for', selectedKey, res.data.id);
			} catch (err) {
				console.warn('Failed to save privacy notice to server', err);
				try { Swal.fire({ icon: 'error', title: 'Save Failed', text: (err.response?.data?.detail || err.message), customClass: { popup: 'swal-popup-custom' } }); } catch (e) { }
			}
		})();
		Swal.fire({ icon: 'success', title: 'Saved', text: 'Notification saved locally', customClass: { popup: 'swal-popup-custom' } });
	};

	const handleSend = async () => {
		if (!selectedKey) return Swal.fire({ icon: 'warning', title: 'No selection', text: 'Select a consent first', customClass: { popup: 'swal-popup-custom' } });
		setLoadingSend(true);
		try {
			const payload = {
				tenant_id: Number(localStorage.getItem('tenant_id')) || null,
				consent_type: selectedKey,
				consent_message: contentMap[selectedKey] || editorValue || '',
				consent_metadata: '',
				declaration_signature: localStorage.getItem('declaration_signature') || 'Whitehats Technologies'
			};
			await axios.post(`${API_BASE}/manager/create-consent`, payload);
			Swal.fire({ icon: 'success', title: 'Submitted', text: 'Notification send/create request submitted', customClass: { popup: 'swal-popup-custom' } });
		} catch (e) {
			console.error(e);
			Swal.fire({ icon: 'error', title: 'Failed', text: 'Failed to send notification', customClass: { popup: 'swal-popup-custom' } });
		} finally {
			setLoadingSend(false);
		}
	};

	const closeModal = () => {
		setModalOpen(false);
		setModalKey(null);
		setModalMode('view');
	};

	const handleModalSave = async () => {
		if (!modalKey) return Swal.fire({ icon: 'warning', title: 'No selection', text: 'No consent selected', customClass: { popup: 'swal-popup-custom' } });
		if (isNoticeLocked(modalKey)) return Swal.fire({ icon: 'info', title: 'Notice Locked', text: 'Accepted and locked notices cannot be edited. Create a new version instead.', customClass: { popup: 'swal-popup-custom' } });
		const safe = sanitize(editorValue);
		const currentVersion = metaMap[modalKey]?.version || '1.0';
		const versionPlan = getConsentVersionUpdatePlan(currentVersion, editVersionValue);
		const effectiveVer = versionPlan.effectiveVersion || currentVersion;

		// update local maps immediately for responsive UI
		setContentMap(prev => ({ ...prev, [modalKey]: safe }));
		setMetaMap(prev => ({
			...prev,
			[modalKey]: {
				...(prev[modalKey] || {}),
				description: safe,
				label: (prev[modalKey] && prev[modalKey].label) || modalKey,
				version: effectiveVer,
				status: 'under_process',
				locked: false
			}
		}));
		localStorage.setItem('last_saved_notification', JSON.stringify({ consent_type: modalKey, consent_message: safe }));
		localStorage.setItem('selected_consent_for_notification', JSON.stringify({ consent_type: modalKey, consent_message: safe }));

		// Persist to backend (await so we can refresh ids/rows)
		try {
			const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
			if (!tenant_id) {
				console.warn('No tenant_id in localStorage; cannot save privacy notice to server');
				Swal.fire({ icon: 'error', title: 'Tenant not set', text: 'Sign in or set tenant before saving to database', customClass: { popup: 'swal-popup-custom' } });
				return;
			}
			const created_by = Number(localStorage.getItem('user_id')) || null;
			const payload = {
				tenant_id,
				title: (metaMap[modalKey] && metaMap[modalKey].label) || modalKey,
				content: safe,
				description: safe,
				consent_type: modalKey,
				consent_message: safe,
				version: effectiveVer,
				consent_version: effectiveVer,
				previous_version: currentVersion,
				reject_previous: versionPlan.shouldRejectPreviousVersion,
				status: 'under_process',
				html: safe,
				card_data: {
					format: 'pdf_cached',
					usage: (metaMap[modalKey] && metaMap[modalKey].usage) || 0,
					color: (metaMap[modalKey] && metaMap[modalKey].color) || null,
					label: (metaMap[modalKey] && metaMap[modalKey].label) || modalKey,
					version: effectiveVer
				},
				created_by: created_by
			};
			let res;
			const existingId = noticeIdMap[modalKey];
			if (existingId) {
				res = await axios.put(`${API_BASE}/manager/privacy-notices/${existingId}`, payload);
			} else {
				try {
					res = await axios.post(`${API_BASE}/manager/privacy-notices`, payload);
				} catch (err) {
					if (err.response && err.response.status === 400 && (err.response.data?.detail || '').toString().toLowerCase().includes('already exists')) {
						await fetchNotices();
						const found = Object.entries(noticeIdMap || {}).find(([k, v]) => (k || '').toString().trim().toLowerCase() === (modalKey || '').toString().trim().toLowerCase());
						const resolvedId = found ? found[1] : null;
						if (resolvedId) {
							res = await axios.put(`${API_BASE}/manager/privacy-notices/${resolvedId}`, payload);
						} else {
							throw err;
						}
					} else {
						throw err;
					}
				}
			}
			if (res && res.data && res.data.success) {
				const parentId = consentParentIdMap[normalizeKey(modalKey)];
				if (parentId) {
					await axios.post(`${API_BASE}/manager/update-new-consent`, {
						id: parentId,
						tenant_id,
						consent_type: modalKey,
						consent_message: safe,
						consent_version: effectiveVer,
						status: 'under_process',
						consent_metadata: JSON.stringify({ label: metaMap[modalKey]?.label || modalKey, version: effectiveVer }),
					}, { headers: { 'X-Tenant-ID': tenant_id } });
				}
				setConsentStateMap(prev => ({
					...prev,
					[normalizeKey(modalKey)]: {
						...(prev[normalizeKey(modalKey)] || {}),
						status: 'under_process',
						version: effectiveVer,
						locked: false
					}
				}));
				// update noticeIdMap if backend returned an id
				if (res.data.id) setNoticeIdMap(prev => ({ ...prev, [modalKey]: res.data.id }));
				// refresh server list to sync active flags and ids
				await fetchNotices();
			}
		} catch (err) {
			console.warn('Failed to save privacy notice to server', err);
			try { Swal.fire({ icon: 'error', title: 'Save Failed', text: (err.response?.data?.detail || err.message), customClass: { popup: 'swal-popup-custom' } }); } catch (e) { }
		}

		// generate and cache PDF (best-effort, don't block UI)
		(async () => {
			try {
				const body = new URLSearchParams();
				body.append('consent_type', modalKey);
				body.append('message', safe);
				if (effectiveTenantId) body.append('tenant_id', String(effectiveTenantId));
				const pres = await axios.post(`${API_BASE}/manager/consent/template_pdf_custom`, body, { responseType: 'blob', headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
				if (pres && pres.data) {
					const blob = pres.data;
					const reader = new FileReader();
					reader.onload = function (e) {
						try {
							const b64 = e.target.result.split(',')[1];
							const norm = (modalKey || '').toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/gi, '');
							const store = { filename: `${norm || modalKey}.pdf`, data: b64 };
							localStorage.setItem(`notification_pdf_${norm}`, JSON.stringify(store));
							refreshCachedPdfsList();
							console.debug('Cached generated PDF for', modalKey, '->', `notification_pdf_${norm}`);
						} catch (err) { console.warn('Failed to store generated PDF', err); }
					};
					reader.readAsDataURL(blob);
				}
			} catch (e) { console.warn('Failed to generate PDF for saved notification', e); }
		})();

		setModalOpen(false);
		Swal.fire({ icon: 'success', title: 'Saved', customClass: { popup: 'swal-popup-custom' } });
	};

	const handleModalDelete = async () => {
		if (!modalKey) return Swal.fire({ icon: 'warning', title: 'No selection', text: 'No consent selected', customClass: { popup: 'swal-popup-custom' } });
		if (isNoticeLocked(modalKey)) return Swal.fire({ icon: 'info', title: 'Notice Locked', text: 'Accepted and locked notices cannot be deleted. Create a new version instead.', customClass: { popup: 'swal-popup-custom' } });
		// Close the custom edit modal first so Swal dialogs are not stacked behind it.
		const keyToDelete = modalKey;
		setModalOpen(false);
		const result = await Swal.fire({
			title: 'Delete Privacy Notice',
			text: `Delete "${keyToDelete}"? This cannot be undone.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Delete',
			cancelButtonText: 'Cancel',
			confirmButtonColor: '#ef4444',
			customClass: { popup: 'swal-popup-custom' }
		});
		if (!result.isConfirmed) {
			// reopen the modal if user cancels
			setModalKey(keyToDelete);
			setModalMode('edit');
			setModalOpen(true);
			return;
		}
		const norm = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/gi, '');
		const target = norm(keyToDelete);
		try {
			// find existing id by matching normalized keys in noticeIdMap
			let existingId = null;
			for (const [k, v] of Object.entries(noticeIdMap || {})) {
				if (norm(k) === target) { existingId = v; break; }
			}
			if (existingId) {
				const token = localStorage.getItem('token') || null;
				const tenant_id = localStorage.getItem('tenant_id');
				let res;
				try {
					res = await axios.delete(`${API_BASE}/manager/privacy-notices/${existingId}`, {
						headers: token ? { Authorization: `Bearer ${token}` } : {},
						params: { tenant_id: tenant_id ? Number(tenant_id) : null }
					});
				} catch (err) {
					console.warn('Delete request failed', err);
					throw err;
				}
				if (!(res && (res.data && res.data.success))) {
					throw new Error(res?.data?.detail || 'Server failed to delete notice');
				}
			}
			// remove matching keys from maps (compare normalized keys)
			setContentMap(prev => {
				const out = {};
				for (const [k, v] of Object.entries(prev || {})) if (norm(k) !== target) out[k] = v;
				return out;
			});
			setMetaMap(prev => {
				const out = {};
				for (const [k, v] of Object.entries(prev || {})) if (norm(k) !== target) out[k] = v;
				return out;
			});
			setNoticeIdMap(prev => {
				const out = {};
				for (const [k, v] of Object.entries(prev || {})) if (norm(k) !== target) out[k] = v;
				return out;
			});
			setRows(prev => Array.isArray(prev) ? prev.filter(r => norm(r.consent_type || r.title || '') !== target) : []);
			setSelectedKey(prev => (norm(prev) === target ? null : prev));
			// remove any cached PDFs that match the normalized key
			try {
				for (let i = 0; i < localStorage.length; i++) {
					const k = localStorage.key(i);
					if (!k) continue;
					if (k.toString().toLowerCase().includes(`notification_pdf_${target}`)) {
						localStorage.removeItem(k);
					}
				}
				refreshCachedPdfsList();
			} catch (e) { /* ignore */ }
			// refresh list from server
			try { await fetchNotices(); } catch (e) { }
			Swal.fire({ icon: 'success', title: 'Deleted', text: 'Privacy notice has been deleted successfully', customClass: { popup: 'swal-popup-custom' } });
		} catch (err) {
			console.warn('Failed to delete privacy notice', err);
			Swal.fire({ icon: 'error', title: 'Delete Failed', text: (err.response?.data?.detail || err.message), customClass: { popup: 'swal-popup-custom' } });
		}
	};

	const handleSaveAll = async () => {
		const types = Object.keys({ ...metaMap, ...contentMap });
		if (!types || types.length === 0) return Swal.fire({ icon: 'warning', title: 'Nothing to save', text: 'No notification cards to save', customClass: { popup: 'swal-popup-custom' } });
		const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
		if (!tenant_id) return Swal.fire({ icon: 'error', title: 'Tenant not set', text: 'Sign in or set tenant before saving to database', customClass: { popup: 'swal-popup-custom' } });
		const created_by = Number(localStorage.getItem('user_id')) || null;
		let success = 0;
		let failed = 0;
		const errors = [];
		for (const t of types) {
			const safe = sanitize(contentMap[t] || '');
			// const payload = {
			const payload = {
				tenant_id,
				title: t,
				// consent: safe 
				content: safe,
				description: safe,
				consent_type: t,
				consent_message: safe,
				html: safe,
				card_data: {
					format: 'pdf_cached',
					usage: (metaMap[t] && metaMap[t].usage) || 0,
					color: (metaMap[t] && metaMap[t].color) || null,
					label: (metaMap[t] && metaMap[t].label) || t
				},
				created_by: created_by
			};
			try {
				// const res = await axios.post(`${API_BASE}/manager/privacy-notices`, payload);
				const existingId = noticeIdMap[t];
				let res;
				if (existingId) {
					res = await axios.put(`${API_BASE}/manager/privacy-notices/${existingId}`, payload);
				} else {
					try {
						res = await axios.post(`${API_BASE}/manager/privacy-notices`, payload);
					} catch (err) {
						if (err.response && err.response.status === 400 && (err.response.data?.detail || '').toString().toLowerCase().includes('already exists')) {
							// refresh notices and attempt to find the existing id
							await fetchNotices();
							const found = Object.entries(noticeIdMap || {}).find(([k, v]) => (k || '').toString().trim().toLowerCase() === (t || '').toString().trim().toLowerCase());
							const resolvedId = found ? found[1] : null;
							if (resolvedId) {
								res = await axios.put(`${API_BASE}/manager/privacy-notices/${resolvedId}`, payload);
							} else {
								throw err;
							}
						} else {
							throw err;
						}
					}
				}
				if (res && res.data && res.data.success) {
					success += 1;
				} else {
					failed += 1;
					errors.push(`${t}: no success`);
				}
			} catch (err) {
				failed += 1;
				errors.push(`${t}: ${err.response?.data?.detail || err.message}`);
			}
		}
		Swal.fire({
			icon: failed ? 'warning' : 'success',
			title: 'Save All',
			text: `Saved ${success}/${types.length} notices. ${failed} failed.` + (errors.length ? ' Errors: ' + errors.join('; ') : ''),
			customClass: { popup: 'swal-popup-custom' }
		});
	};

	// Build displayTypes from `rows` (privacy_notices fetched from server).
	// This ensures only notices created/stored in `privacy_notices` are shown.
	let displayTypes = [];
	try {
		if (Array.isArray(rows) && rows.length > 0) {
			const list = [];
			for (const r of rows) {
				const k = r.id ? String(r.id) : (r.consent_type || r.title || '').toString().trim();
				if (!k) continue;
				if (!list.includes(k)) list.push(k);
			}
			displayTypes = list;
		}
	} catch (e) {
		displayTypes = [];
	}
	if (!displayTypes || displayTypes.length === 0) displayTypes = ['bank', 'patient', 'insurance', 'general', 'legal', 'hotel_visitor', 'manufacturing_visitor'];

	return (
		<div style={styles.container}>
			<div style={styles.header}>
				<div style={styles.headerLeft}>
					<h2 style={styles.title}>Privacy Notices</h2>
				</div>
				<div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
					{/* <button style={styles.saveBtn} onClick={() => handleSaveAll()}>Save All</button> */}
					<button className="tour-target-notif-saveall" style={styles.saveBtn} onClick={() => handleSaveAll()}>Save All</button>
					{/* <button style={styles.createBtn} onClick={() => setNewModalOpen(true)}>Add Notices</button> */}
					<button className="tour-target-notif-add" style={styles.createBtn} onClick={() => setNewModalOpen(true)}>Add Notices</button>
					{/* <button style={{ ...styles.createBtn, background: '#ef4444', borderColor: '#ef4444' }} onClick={() => {
						setNewModalOpen(true);
						setNewTitle('NOTICE OF DATA BREACH');
						setNewConsentType('data_breach');
						setNewIsCustom(false);
						setNewContentVal(dataBreachTemplate);
					}}>Add Data Breach</button> */}
					<button className="tour-target-notif-back" style={styles.backBtn} onClick={() => {
						// <button style={styles.backBtn} onClick={() => {
						if (typeof onBack === 'function') {
							onBack();
						} else {
							window.history.back();
						}
					}}>Back</button>
				</div>
			</div>

			<div style={styles.gridContainer}>
				{/* <div style={styles.grid}> */}
				<div className="tour-target-notif-grid" style={styles.grid}>
					{displayTypes.map(key => {
						const meta = metaMap[key] || { color: '#2563eb', usage: 10, status: 'under_process', locked: false };
						const rawCt = meta.consent_type || key;
						const noticeIdStr = meta.id ? String(meta.id) : null;
						const labelStr = meta.label ? normalizeKey(meta.label) : null;

						const nk1 = normalizeKey(rawCt);
						const nk2 = normalizeKey(key);
						const nk3 = String(key).trim().toLowerCase();

						const stateInfo = consentStateMap[nk1] || consentStateMap[nk2] || consentStateMap[nk3] || (noticeIdStr ? consentStateMap[noticeIdStr] : null) || (labelStr ? consentStateMap[labelStr] : null) || {};
						const currentStatus = (stateInfo.status || meta.status || 'under_process').toString().toLowerCase();
						const locked = Boolean(stateInfo.locked ?? (currentStatus === 'accepted'));
						const noticeState = { id: stateInfo.id || meta.id, status: locked ? 'accepted' : 'under_process', version: stateInfo.version || meta.version || version || '1.0', locked };
						const isSelected = selectedKey === key;
						return (<div
							key={key}
							style={{
								...styles.card,
								padding: '1.25rem',
								position: 'relative',
								alignItems: 'stretch',
								justifyContent: 'flex-start',
								textAlign: 'left',
								...(isSelected ? styles.cardSelected : {}),
							}}
							onClick={() => handleCardClick(key)}
							onMouseEnter={() => setHoveredCard(key)}
							onMouseLeave={() => setHoveredCard(null)}
						>
							{/* Card Top Row: Dynamic Icon Container Left, Action Buttons Right */}
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '0.85rem' }}>
								<div style={{
									width: '42px',
									height: '42px',
									borderRadius: '12px',
									background: `${meta.color || '#2563eb'}18`,
									color: meta.color || '#2563eb',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									flexShrink: 0
								}}>
									{getNoticeCardIcon(key, meta.color)}
								</div>

								<div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
									<button
										className="tour-target-notif-view"
										type="button"
										title="View Notice"
										style={{ ...styles.actionBtn, padding: '6px', borderRadius: '8px' }}
										onClick={(e) => {
											e.stopPropagation();
											setModalMode('view');
											setModalKey(key);
											setModalOpen(true);
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.background = '#2563eb';
											e.currentTarget.style.color = '#fff';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.background = '#f1f5f9';
											e.currentTarget.style.color = '#64748b';
										}}
									>
										<Eye size={15} />
									</button>

									{!locked && <button
										className="tour-target-notif-edit"
										type="button"
										title="Edit Notice"
										style={{ ...styles.actionBtn, padding: '6px', borderRadius: '8px' }}
										onClick={(e) => {
											e.stopPropagation();
											setModalMode('edit');
											setModalKey(key);
											setEditorValue(contentMap[key] || '');
											setEditVersionValue(metaMap[key]?.version || '1.0');
											setModalOpen(true);
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.background = '#10b981';
											e.currentTarget.style.color = '#fff';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.background = '#f1f5f9';
											e.currentTarget.style.color = '#64748b';
										}}
									>
										<Edit size={15} />
									</button>
									}

									<button
										type="button"
										title={locked ? 'Accepted and locked' : 'Accept and lock notice'}
										style={{ ...styles.actionBtn, padding: '6px', borderRadius: '8px', color: locked ? '#64748b' : '#059669', opacity: locked ? 0.65 : 1 }}
										disabled={locked}
										onClick={(e) => { e.stopPropagation(); handleAcceptNotice(key); }}
									>
										{locked ? <Lock size={15} /> : <CheckCircle size={15} />}
									</button>

									{/* <button
										className="tour-target-notif-fileText"
										type="button"
										title="Sub-categories"
										style={{ ...styles.actionBtn, padding: '6px', borderRadius: '8px' }}
										onClick={(e) => {
											e.stopPropagation();
											setModalMode('sub');
											setModalKey(key);
											setModalOpen(true);
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.background = '#7c3aed';
											e.currentTarget.style.color = '#fff';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.background = '#f1f5f9';
											e.currentTarget.style.color = '#64748b';
										}}
									>
										<FileText size={15} />
									</button> */}

									<button
										className="tour-target-notif-multilingual"
										type="button"
										title={locked ? 'Multi-language versions' : 'Accept and lock notice first'}
										style={{ ...styles.actionBtn, padding: '6px', borderRadius: '8px', opacity: locked ? 1 : 0.45, cursor: locked ? 'pointer' : 'not-allowed' }}
										disabled={!locked}
										onClick={(e) => {
											e.stopPropagation();
											if (!locked) return;
											setMultilingualTarget({ key, id: consentParentIdMap[normalizeKey(key)] || null, label: metaMap[key]?.label || key });
											setMultilingualOpen(true);
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.background = '#0284c7';
											e.currentTarget.style.color = '#fff';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.background = '#f1f5f9';
											e.currentTarget.style.color = '#64748b';
										}}
									>
										<Languages size={15} />
									</button>

									{!locked && (metaMap[key]?.id || noticeIdMap[key]) && (
										<button
											type="button"
											title="Delete Notice"
											style={{ ...styles.actionBtn, padding: '6px', borderRadius: '8px', color: '#ef4444' }}
											onClick={(e) => {
												e.stopPropagation();
												const noticeId = metaMap[key]?.id || noticeIdMap[key];
												if (!noticeId) return;
												Swal.fire({
													title: 'Delete Notice?',
													text: 'Are you sure you want to delete this privacy notice?',
													icon: 'warning',
													showCancelButton: true,
													confirmButtonColor: '#ef4444',
													confirmButtonText: 'Delete'
												}).then(async (result) => {
													if (result.isConfirmed) {
														try {
															const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
															await axios.delete(`${API_BASE}/manager/privacy-notices/${noticeId}`, { params: { tenant_id } });
															await fetchNotices();
															Swal.fire({ icon: 'success', title: 'Deleted', text: 'Privacy notice has been deleted', customClass: { popup: 'swal-popup-custom' } });
														} catch (err) {
															Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.detail || 'Failed to delete notice', customClass: { popup: 'swal-popup-custom' } });
														}
													}
												});
											}}
											onMouseEnter={(e) => {
												e.currentTarget.style.background = '#ef4444';
												e.currentTarget.style.color = '#fff';
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.background = '#f1f5f9';
												e.currentTarget.style.color = '#ef4444';
											}}
										>
											<Trash2 size={15} />
										</button>
									)}
								</div>
							</div>

							{/* Notice Title */}
							<div style={{ ...styles.cardTitle, textAlign: 'left', marginBottom: '0.45rem', fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', wordBreak: 'break-word', overflowWrap: 'anywhere', wordWrap: 'break-word', maxWidth: '100%', minWidth: 0 }}>
								{meta.label || key}
							</div>
							<div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 5, marginBottom: '0.6rem', padding: '4px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, color: locked ? '#15803d' : '#b45309', background: locked ? '#dcfce7' : '#fef3c7', border: `1px solid ${locked ? '#86efac' : '#fbbf24'}` }}>
								{locked ? <Lock size={12} /> : <span>✏️</span>}
								{locked ? 'ACCEPTED' : 'UNDER PROCESS'}
							</div>

							{/* Notice Content Message Preview */}
							<div style={{ ...styles.cardDesc, textAlign: 'left', marginBottom: '1.1rem', color: '#475569', fontSize: '0.875rem', lineHeight: 1.5 }}>
								{previewText(contentMap[key] || metaMap[key]?.description || '') || 'No content available'}
							</div>

							{/* Footer: Category Pill Left + Notice Version Tag Right */}
							<div style={{ marginTop: 'auto', paddingTop: '0.65rem', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
								<span style={{ fontSize: '0.72rem', fontWeight: 600, color: meta.color || '#2563eb', background: `${meta.color || '#2563eb'}12`, padding: '2px 8px', borderRadius: '9999px' }}>
									{(metaMap[key]?.consent_type || key).replace(/_/g, ' ')}
								</span>

								<a
									href="/version"
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										if (locked) {
											setVersionManagementTarget({
												id: noticeState.id || meta.id || noticeIdMap[key],
												consent_type: rawCt || key,
												type: rawCt || key,
												name: meta.label || key,
												consent_message: contentMap[key] || meta.description || '',
												consent_version: noticeState.version,
												status: noticeState.status || 'accepted',
											});
											return;
										}
										setVersionModalTarget({
											key,
											label: meta.label || key,
											version: meta.version || version || '1.0',
											color: meta.color,
											description: contentMap[key] || meta.description || ''
										});
										setVersionModalOpen(true);
									}}
									style={{
										fontSize: '0.76rem',
										fontWeight: 600,
										color: '#475569',
										background: '#f8fafc',
										border: '1px solid #e2e8f0',
										padding: '2px 8px',
										borderRadius: '6px',
										textDecoration: 'none',
										display: 'inline-flex',
										alignItems: 'center',
										gap: '4px'
									}}
									title="Notice Version"
								>
									<span style={{ color: '#94a3b8', fontWeight: 500 }}>ver</span> v{noticeState.version}
								</a>
							</div>
						</div>
						);
					})}
				</div>

				{/* <div style={{ flex: 1, marginLeft: '24px', minWidth: '420px' }}>
					<div style={{ marginBottom: '12px', fontSize: '14px', color: '#475569' }}>Editor / Preview</div>
					<ReactQuill
						theme="snow"
						value={editorValue}
						onChange={setEditorValue}
						modules={quillModules}
						formats={quillFormats}
						style={{ height: '240px' }}
					/>
					<div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
						<button style={styles.saveBtn} onClick={handleSave}>Save</button>
						<button style={styles.cancelBtn} onClick={() => { setEditorValue(contentMap[selectedKey] || ''); }}>Reset</button>
						<button style={styles.createBtn} onClick={handleSend} disabled={loadingSend}>{loadingSend ? 'Sending...' : 'Send'}</button>
					</div>

					<div style={{ marginTop: '18px' }}>
						<div style={{ fontSize: '13px', color: '#64748b' }}>Preview</div>
						<div className="cookie-consent-widget-preview" style={{ marginTop: '8px' }} dangerouslySetInnerHTML={{ __html: sanitize(contentMap[selectedKey] || editorValue || '') }} />
					</div>
				</div> */}

				{modalOpen && modalKey && (
					<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200 }} onClick={closeModal}>
						<div style={{ width: '720px', maxWidth: '94vw', margin: '6vh auto', background: '#fff', borderRadius: 8, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eef2f7' }}>
								<h3 style={{ margin: 0, fontSize: 16 }}>
									{modalMode === 'view' ? `View: ${metaMap[modalKey]?.label || modalKey}` : modalMode === 'edit' ? `Edit: ${metaMap[modalKey]?.label || modalKey}` : `Sub-categories: ${metaMap[modalKey]?.label || modalKey}`}
								</h3>
								<button onClick={closeModal} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer' }}>×</button>
							</div>
							<div style={{ padding: 16, maxHeight: '60vh', overflowY: 'auto' }}>
								{modalMode === 'edit' ? (
									<>
										<div style={{ marginBottom: 14, background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
											<label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#334155', fontSize: 13.5 }}>
												Notice Version Tag
											</label>
											<div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
												<input
													type="text"
													value={editVersionValue}
													onChange={e => setEditVersionValue(e.target.value)}
													placeholder="e.g. 1.1 or 2.0"
													style={{
														width: '200px',
														padding: '8px 12px',
														borderRadius: 6,
														border: '1px solid #cbd5e1',
														fontSize: 14,
														fontWeight: 600,
														color: '#0f172a'
													}}
												/>
												<span style={{ fontSize: 12, color: '#64748b' }}>
													(Current Active: v{metaMap[modalKey]?.version || '1.0'})
												</span>
											</div>
											<div style={{ fontSize: 11.5, color: '#64748b', marginTop: 4 }}>
												Updating the version tag will reject/supersede the previous version and activate the new version.
											</div>
										</div>

										<div style={{ marginBottom: 6 }}>
											<label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#334155', fontSize: 13.5 }}>
												Notice Content
											</label>
										</div>
										<ReactQuill theme="snow" value={editorValue} onChange={setEditorValue} modules={quillModules} formats={quillFormats} />
									</>
								) : (
									<div dangerouslySetInnerHTML={{ __html: sanitize(contentMap[modalKey] || metaMap[modalKey]?.description || '') }} />
								)}
							</div>
							{modalMode === 'edit' && (
								<div style={{ padding: 12, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eef2f7' }}>
									<button style={{ ...styles.cancelBtn, flex: 'none', minWidth: 110 }} onClick={closeModal}>Cancel</button>
									<button
										style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer', minWidth: 110, flex: 'none' }}
										onClick={handleModalDelete}
									>
										Delete
									</button>
									<button style={{ ...styles.saveBtn, flex: 'none', minWidth: 110 }} onClick={handleModalSave}>Save</button>
								</div>
							)}


						</div>
					</div>
				)}

				{/* New Privacy modal - rendered independently so button works */}
				{newModalOpen && (
					<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200 }} onClick={() => setNewModalOpen(false)}>
						<div style={{ width: '720px', maxWidth: '94vw', margin: '6vh auto', background: '#fff', borderRadius: 8, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eef2f7' }}>
								<h3 style={{ margin: 0, fontSize: 16 }}>Create Privacy Notice</h3>
								<button onClick={() => setNewModalOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer' }}>×</button>
							</div>
							<div style={{ padding: 16, maxHeight: '60vh', overflowY: 'auto', boxSizing: 'border-box' }}>
								{/* <div style={{ marginBottom: 8 }}>
										<label style={{ display: 'block', marginBottom: 6 }}>Title</label>
										<input value={newTitle} onChange={e => setNewTitle(e.target.value)} style={{ width: '100%', padding: 8 }} />
									</div> */}
								<div style={{ marginBottom: 12 }}>
									<label style={{ display: 'block', marginBottom: 6, color: '#334155', fontSize: 14 }}>Title</label>
									<input
										value={newTitle}
										onChange={e => setNewTitle(e.target.value)}
										placeholder="Enter title"
										style={{
											width: '100%',
											padding: '10px 12px',
											background: '#ffffff',
											color: '#0f172a',
											border: '1px solid #cbd5e1',
											borderRadius: 6,
											boxSizing: 'border-box',
											fontSize: 14,
											outline: 'none'
										}}
									/>
								</div>
								<div style={{ marginBottom: 8 }}>
									<label style={{ display: 'block', marginBottom: 6 }}>Consent Type</label>
									{
										(() => {
											const combined = Array.from(new Set([
												...(consentTypesList || []),
												...rows.map(r => r.consent_type).filter(Boolean),
												'bank', 'patient', 'insurance', 'general', 'legal', 'hotel_visitor', 'manufacturing_visitor'
											])).filter(ct => ct && !/^\d+$/.test(ct));
											return (
												<select value={newConsentType || ''} onChange={(e) => { const v = e.target.value; setNewConsentType(v); setNewIsCustom(v === 'custom'); }} style={{ width: '100%', padding: 8 }}>
													<option value="">-- Select existing or choose Custom --</option>
													<option value="custom">-- Custom consent type --</option>
													{combined.map(dt => <option key={dt} value={dt}>{dt}</option>)}
												</select>
											);
										})()
									}
									{newIsCustom && (
										<input value={newConsentType === 'custom' ? '' : newConsentType} onChange={e => setNewConsentType(e.target.value)} placeholder="Enter custom consent type" style={{ width: '100%', padding: 8, marginTop: 8 }} />
									)}
								</div>
								<div style={{ marginBottom: 8 }}>
									<label style={{ display: 'block', marginBottom: 6 }}>Content</label>
									<ReactQuill theme="snow" value={newContentVal} onChange={setNewContentVal} modules={quillModules} formats={quillFormats} />
								</div>
							</div>
							<div style={{ padding: 12, display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #eef2f7' }}>
								<button
									type="button"
									style={{ background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
									onClick={() => {
										const candidateRaw = newIsCustom ? (newConsentType || newTitle) : (newConsentType || newTitle);
										const candidateKey = normalizeKey(candidateRaw || '');
										if (candidateKey) {
											setMultilingualTarget({ key: candidateKey, label: newTitle || newConsentType || candidateKey });
											setMultilingualOpen(true);
										} else {
											Swal.fire({ icon: 'info', title: 'Title Required', text: 'Please enter a title or select consent type first', customClass: { popup: 'swal-popup-custom' } });
										}
									}}
								>
									<Languages size={15} /> Multi-Language Versions
								</button>
								<div style={{ display: 'flex', gap: 8 }}>
									<button style={styles.cancelBtn} onClick={() => setNewModalOpen(false)}>Cancel</button>
									<button style={styles.saveBtn} onClick={async () => {
										try {
											const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
											if (!tenant_id) return Swal.fire({ icon: 'error', title: 'Tenant not set', text: 'Sign in or set tenant before saving to database', customClass: { popup: 'swal-popup-custom' } });
											const created_by = Number(localStorage.getItem('user_id')) || null;
											const candidateRaw = newIsCustom ? (newConsentType || newTitle) : (newConsentType || newTitle);
											const candidateKey = normalizeKey(candidateRaw || '');
											if (!candidateKey) return Swal.fire({ icon: 'warning', title: 'Missing info', text: 'Please enter a title or consent type', customClass: { popup: 'swal-popup-custom' } });
											const candidateTitleNorm = (newTitle || candidateRaw || '').trim().toLowerCase();
											const exists = Array.isArray(rows) && rows.some(r => {
												const rType = normalizeKey((r.consent_type || '').toString());
												const rTitle = (r.title || r.consent_type || '').trim().toLowerCase();
												return rType === candidateKey && rTitle === candidateTitleNorm;
											});
											if (exists) return Swal.fire({ icon: 'warning', title: 'Duplicate', text: 'A privacy notice with this title and consent type already exists for this tenant', customClass: { popup: 'swal-popup-custom' } });
											const payload = {
												tenant_id,
												title: newTitle || (newConsentType === 'custom' ? newConsentType : newConsentType) || 'Untitled',
												content: newContentVal || '',
												description: newContentVal || '',
												consent_type: candidateKey,
												consent_message: newContentVal || '',
												html: newContentVal || null,
												card_data: { format: 'pdf_cached', usage: 0, color: null, label: newTitle || newConsentType },
												created_by,
												declaration_signature: localStorage.getItem('declaration_signature') || 'Whitehats Technologies'
											};
											const res = await axios.post(`${API_BASE}/manager/privacy-notices`, payload);
											if (res && res.data && res.data.success) {
												// Keep the consent hierarchy in sync so multilingual variants have
												// a real new_consent parent, even when the notice was created here.
												try {
													await axios.post(`${API_BASE}/manager/new-consent`, {
														tenant_id,
														consent_type: candidateKey,
														consent_message: newContentVal || '',
														consent_metadata: JSON.stringify({ label: newTitle || candidateKey }),
														status: 'under_process',
													}, { headers: { 'X-Tenant-ID': tenant_id } });
												} catch (syncError) {
													console.warn('Failed to sync notice to consent hierarchy', syncError);
												}
												setNewModalOpen(false);
												setNewTitle(''); setNewContentVal(''); setNewConsentType('');
												// refresh notices
												await fetchNotices();
												Swal.fire({ icon: 'success', title: 'Created', text: 'Privacy notice created', customClass: { popup: 'swal-popup-custom' } });
											} else {
												Swal.fire({ icon: 'error', title: 'Failed', text: 'Failed to create notice', customClass: { popup: 'swal-popup-custom' } });
											}
										} catch (err) {
											console.warn('Create privacy notice failed', err);
											Swal.fire({ icon: 'error', title: 'Failed', text: (err.response?.data?.detail || err.message), customClass: { popup: 'swal-popup-custom' } });
										}
									}}>Create</button>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Multilingual Notice Manager Modal */}
				{multilingualOpen && multilingualTarget && (
					<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1300, overflowY: 'auto' }}>
						<MultilingualConsentPage
							parentConsent={{ id: multilingualTarget.id || noticeIdMap[multilingualTarget.key] || metaMap[multilingualTarget.key]?.id || null, key: multilingualTarget.key, name: multilingualTarget.label, consent_type: multilingualTarget.key, label: multilingualTarget.label }}
							tenantId={Number(localStorage.getItem('tenant_id')) || 0}
							token={localStorage.getItem('token') || ''}
							onBack={() => setMultilingualOpen(false)}
						/>
					</div>
				)}

				{/* Notice Version Details Modal */}
				{versionModalOpen && versionModalTarget && (
					<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setVersionModalOpen(false)}>
						<div style={{ width: '520px', maxWidth: '92vw', background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
							<div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									<span style={{ fontSize: '18px' }}>📋</span>
									<h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>Notice Version Details</h3>
								</div>
								<button onClick={() => setVersionModalOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}>×</button>
							</div>

							<div style={{ padding: '20px' }}>
								<div style={{ marginBottom: '16px', padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<div>

										<div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Active Version</div>
										<div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#15803d', marginTop: '2px' }}>v{versionModalTarget.version}</div>
									</div>
									<span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '4px 10px', borderRadius: '9999px' }}>✓ ACTIVE</span>
								</div>

								<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem', color: '#334155' }}>
									<div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
										<span style={{ color: '#64748b' }}>Notice Title:</span>
										<strong>{versionModalTarget.label}</strong>
									</div>
									<div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
										<span style={{ color: '#64748b' }}>Consent Type:</span>
										<code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', color: '#475569' }}>{versionModalTarget.key}</code>
									</div>
									<div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
										<span style={{ color: '#64748b' }}>Previous Version Policy:</span>
										<span style={{ color: '#ef4444', fontWeight: 600, fontSize: '0.85rem' }}>✕ Rejected & Superseded on Update</span>
									</div>
								</div>
							</div>

							<div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
								<button
									style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer' }}
									onClick={() => setVersionModalOpen(false)}
								>
									Close
								</button>
								<button
									style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
									onClick={() => {
										const keyToEdit = versionModalTarget.key;
										setVersionModalOpen(false);
										setModalMode('edit');
										setModalKey(keyToEdit);
										setEditorValue(contentMap[keyToEdit] || '');
										setEditVersionValue(metaMap[keyToEdit]?.version || '1.0');
										setModalOpen(true);
									}}
								>
									Edit Notice & Version
								</button>
							</div>
						</div>
					</div>
				)}

				{versionManagementTarget && (
					<VersionManagementModal
						item={versionManagementTarget}
						mode="main"
						tenantId={Number(localStorage.getItem('tenant_id')) || 0}
						token={localStorage.getItem('token') || ''}
						onClose={() => setVersionManagementTarget(null)}
						onUpdated={async () => { setVersionManagementTarget(null); await fetchNotices(); }}
					/>
				)}
			</div>
		</div>
	);
}

export default NotificationPage;